import { useState, useEffect } from 'react';
import { getSiren } from '~/runtime';
import { EntityNotificationSnoozeEntity } from '~/old/notifications/types/entity-notification-snooze-entity';
import { IssueEntity } from '~/shared/issues/issue-entity';

/**
 * Hook to filter issues based on their organization's snooze status.
 *
 * @param issues - Array of issue entities to filter
 * @param hideSnoozed - Whether to hide snoozed issues
 * @returns Object containing filtered issues and loading state
 */
export function useFilteredIssues(issues: IssueEntity[], hideSnoozed: boolean) {
  const [filteredIssues, setFilteredIssues] = useState<IssueEntity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hideSnoozed) {
      setFilteredIssues(issues);
      setLoading(false);
      return;
    }

    const fetchSnoozedOrgs = async () => {
      setLoading(true);
      const filtered: IssueEntity[] = [];

      try {
        await Promise.all(issues.map(async (issue) => {
          const orgId = issue.getLinkByRel('https://api.binsentry.com/rel/organization')?.href;

          if (!orgId) {
            // If there's no organization link, we can't check snoozes; keep the issue.
            filtered.push(issue);
            return;
          }
          const orgEntity = await getSiren(orgId);
          const snoozesLink = orgEntity?.getLinkByRel('https://api.binsentry.com/rel/entity-notification-snoozes');

          if (snoozesLink?.href) {
            const snoozesEntity = await getSiren({ href: snoozesLink.href });

            // If there are snooze entities, mark this org as snoozed
            if (!snoozesEntity?.entities || snoozesEntity.entities.length === 0) {
              filtered.push(issue);
              return;
            }

            const snoozeEntity: EntityNotificationSnoozeEntity | undefined = snoozesEntity?.entities?.[0];

            if (!snoozeEntity || !snoozeEntity?.properties?.isActive) {
              filtered.push(issue);
              return;
            }
          } else {
            // If there is no snoozes link, treat the issue as not snoozed
            filtered.push(issue);
            return;
          }
        }));

        setFilteredIssues(filtered);
      } catch (error) {
        console.error('Error when fetching snoozes for issues', error);
        setFilteredIssues(issues);
      }
      finally {
        setLoading(false);
      }
    };

    fetchSnoozedOrgs();
  }, [issues, hideSnoozed]);

  return { filteredIssues, loading };
}
